import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, Pin, Megaphone, Crown, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const URL_SPLIT = /(https?:\/\/[^\s]+)/g;

export const Linkified = ({ text }) => (
  <>{text.split(URL_SPLIT).map((part, i) => (/^https?:\/\//.test(part)
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">{part}</a>
    : <React.Fragment key={i}>{part}</React.Fragment>))}</>
);

const time = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const Bubble = ({ m, mine }) => {
  if (m.kind === 'broadcast') return (
    <div className="mx-auto max-w-[90%] p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid={`chat-broadcast-${m.id}`}>
      <p className="text-[11px] font-bold text-amber-800 uppercase tracking-[0.06em] flex items-center gap-1"><Megaphone size={12} /> {m.author_name} · organizer</p>
      <p className="text-sm text-[#0A0A0A] mt-1 whitespace-pre-wrap"><Linkified text={m.text} /></p>
      <p className="text-[10px] text-amber-700 mt-1">{time(m.created_at)}</p>
    </div>
  );
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${m.id}`}>
      <div className={`max-w-[80%] px-3 py-2 rounded-lg ${mine ? 'bg-[#002FA7] text-white' : 'bg-[#F4F4F5] text-[#0A0A0A]'}`}>
        {!mine && <p className="text-[11px] font-bold text-[#52525B] flex items-center gap-1">{m.author_name}{m.author_role === 'leader' && <Crown size={10} className="text-[#F59E0B]" />}</p>}
        <p className="text-sm whitespace-pre-wrap break-words"><Linkified text={m.text} /></p>
        <p className={`text-[10px] mt-0.5 ${mine ? 'text-white/70' : 'text-[#A1A1AA]'}`}>{time(m.created_at)}</p>
      </div>
    </div>
  );
};

const PinnedBar = ({ pinned, isLeader, onPin }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(pinned?.text || '');
  if (editing) return (
    <div className="flex gap-2 p-2 border-b border-[#E4E4E7] bg-[#FAFAFA]">
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Announcement for your team" className="flex-1 px-2 py-1.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A]" data-testid="chat-pin-input" />
      <button onClick={() => { onPin(text); setEditing(false); }} className="px-3 bg-[#0A0A0A] text-white rounded text-xs font-semibold" data-testid="chat-pin-save-btn">Pin</button>
      <button onClick={() => setEditing(false)} className="px-2 text-[#A1A1AA]"><X size={14} /></button>
    </div>
  );
  if (!pinned && !isLeader) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2 border-b border-[#E4E4E7] bg-[#002FA7]/5" data-testid="chat-pinned">
      <Pin size={13} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
      <p className="text-xs text-[#0A0A0A] flex-1">{pinned ? <><Linkified text={pinned.text} /> <span className="text-[#A1A1AA]">— {pinned.by}</span></> : <span className="text-[#A1A1AA]">No pinned announcement</span>}</p>
      {isLeader && (
        <span className="flex gap-2 text-[11px] font-semibold">
          <button onClick={() => { setText(pinned?.text || ''); setEditing(true); }} className="text-[#002FA7]" data-testid="chat-pin-edit-btn">{pinned ? 'Edit' : 'Pin message'}</button>
          {pinned && <button onClick={() => onPin('')} className="text-red-500" data-testid="chat-unpin-btn">Unpin</button>}
        </span>
      )}
    </div>
  );
};

// Near-live team chat: polls every 3s for messages newer than the last one seen
export const TeamChat = ({ teamId, myUserId, isLeader, headers }) => {
  const [msgs, setMsgs] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const cursor = useRef('');
  const box = useRef(null);
  const url = `${API}/api/event-teams/teams/${teamId}`;

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${url}/chat${cursor.current ? `?after=${encodeURIComponent(cursor.current)}` : ''}`, { headers });
      setPinned(r.data.pinned);
      if (r.data.messages.length) {
        cursor.current = r.data.messages[r.data.messages.length - 1].created_at;
        setMsgs((cur) => { const seen = new Set(cur.map((m) => m.id)); return [...cur, ...r.data.messages.filter((m) => !seen.has(m.id))]; });
      }
    } catch { /* transient */ }
  }, [teamId]);

  useEffect(() => {
    cursor.current = ''; setMsgs([]); poll();
    const id = setInterval(() => { if (!document.hidden) poll(); }, 3000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try { await axios.post(`${url}/chat`, { text }, { headers }); setText(''); await poll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Message not sent'); }
    finally { setSending(false); }
  };
  const pin = async (t) => {
    try { const r = await axios.put(`${url}/pin`, { text: t }, { headers }); setPinned(r.data.pinned); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not pin'); }
  };

  return (
    <div className="rounded-lg border border-[#E4E4E7] overflow-hidden" data-testid="team-chat">
      <div className="px-3 py-2.5 border-b border-[#E4E4E7] flex items-center gap-2">
        <MessageSquare size={15} className="text-[#002FA7]" />
        <p className="text-sm font-bold text-[#0A0A0A]">Team chat</p>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-[#A1A1AA]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> live</span>
      </div>
      <PinnedBar pinned={pinned} isLeader={isLeader} onPin={pin} />
      <div ref={box} className="h-72 overflow-y-auto p-3 space-y-2 bg-white" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-xs text-center text-[#A1A1AA] pt-24">No messages yet — say hi to your team.</p>}
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.author_id === myUserId && m.kind !== 'broadcast'} />)}
      </div>
      <form onSubmit={send} className="flex gap-2 p-2 border-t border-[#E4E4E7]">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Message your team (links work too)"
          className="flex-1 px-3 py-2 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7]" data-testid="chat-input" />
        <button disabled={sending || !text.trim()} className="px-3 bg-[#002FA7] text-white rounded disabled:opacity-40" data-testid="chat-send-btn"><Send size={15} /></button>
      </form>
    </div>
  );
};
